import { useConfigContext } from './AppConfig';
import { setMsalInstance } from './msalInstance';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Public client (PKCE) sign-in against Microsoft Entra ID — the frontend obtains its own
// scope-limited access token and sends it as `Authorization: Bearer` on every backend call
// (specs/001-msal-login/research.md Decision 10). The redirect URI matches the
// "single_page_application" platform registered in init/environment/azure_app.tf, and must be
// handled by a route that stays mounted long enough for MSAL to process the redirect response
// (see Login.tsx).
//
// No Keycloak anywhere under this feature (FR-006), so — unlike an earlier abandoned attempt at
// this file — there is no `knownAuthorities`/`ProtocolMode.OIDC` branch here: the authority is
// always a real, reachable `https://login.microsoftonline.com/...` endpoint in every profile, so
// MSAL's default cloud-instance discovery and AAD protocol mode are exactly right.
export default function Msal({children}: PropsWithChildren) {
    const {t} = useTranslation("login");
    const appConfig = useConfigContext();
    const [instance, setInstance] = useState<PublicClientApplication | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        const pca = new PublicClientApplication({
            auth: {
                clientId: appConfig.msalConfig.clientId,
                authority: appConfig.msalConfig.authority,
                redirectUri: `${window.location.origin}/login`,
            },
            cache: {
                // Cleared on tab close, not shared across tabs — not readable across origins,
                // but still JS-accessible (research.md Decision 10's security tradeoff note).
                cacheLocation: 'sessionStorage',
            },
        });

        pca.initialize()
            .then(() => {
                setMsalInstance(pca);
                setInstance(pca);
            })
            .catch((e: unknown) => {
                console.error("MSAL initialize() failed", e);
                setError(e instanceof Error ? e.message : String(e));
            });
    }, [appConfig]);

    if (error !== undefined) {
        return (
            <div>{t('msal_init_error', {error})}</div>
        )
    }

    if (instance === undefined) {
        return (
            <div>{t('loading')}</div>
        )
    }

    return (
        <MsalProvider instance={instance}>
            {children}
        </MsalProvider>
    )
}
